import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "micButton", "micIcon", "voiceFeedback", "visualizer",
    "responseText", "status", "historyPanel", "messages", "sessionList",
    "form", "textInput", "submitButton",
    "coachStatus", "locationStatus", "locationText", "voiceStatus",
    "coachCard", "coachInfo", "suggestionCard", "suggestions",
    "settingsPanel", "autoSpeakToggle", "voiceSpeed", "voiceSelect", "handsFreeToggle",
    "speakButton", "autoSpeakButton", "contextCards", "responseContainer", "contextPills",
    "textMode", "voiceMode", "modeToggle", "voiceVisualizer", "voiceOnlyText"
  ]

  connect() {
    console.log("🎙️ Voice Chat Controller connected")

    // Initialize mode (default to text)
    this.currentMode = 'text'

    // Initialize speech recognition
    this.initializeSpeechRecognition()

    // Initialize text-to-speech
    this.initializeTextToSpeech()

    // Initialize audio queue for streaming
    this.audioQueue = []
    this.isPlayingQueue = false
    this.audioContextUnlocked = false

    // Load saved preferences
    this.loadPreferences()

    // Set up auto-detection
    this.detectCoachFromHistory()
    this.scanForPartsAndServices()

    // Initialize hands-free if enabled
    if (this.handsFreeEnabled) {
      this.startHandsFreeListening()
    }
  }

  disconnect() {
    this.stopListening()
    this.stopSpeaking()
    this.stopVoiceVisualizer()

    // Clear audio queue
    this.audioQueue = []
    this.isPlayingQueue = false

    if (this.recognition) {
      this.recognition.abort()
    }
  }

  // Speech Recognition Setup
  initializeSpeechRecognition() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    
    if (!SpeechRecognition) {
      console.warn("Speech recognition not supported")
      this.voiceFeedbackTarget.textContent = "Voice input not supported in this browser"
      return
    }

    this.recognition = new SpeechRecognition()
    this.recognition.continuous = true
    this.recognition.interimResults = true
    this.recognition.lang = 'en-US'

    this.recognition.onstart = () => {
      console.log("🎤 Listening...")
      this.updateVoiceStatus("listening")
    }

    this.recognition.onresult = (event) => {
      const last = event.results.length - 1
      const transcript = event.results[last][0].transcript
      const isFinal = event.results[last].isFinal

      if (isFinal) {
        console.log("📝 Final transcript:", transcript)
        this.processVoiceInput(transcript)
      } else {
        if (this.hasVoiceOnlyTextTarget) {
          this.voiceOnlyTextTarget.textContent = transcript
        }
      }
    }

    this.recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error)
      if (this.hasVoiceOnlyTextTarget) {
        this.voiceOnlyTextTarget.textContent = "Voice error. Please try again."
      }
    }

    this.recognition.onend = () => {
      console.log("🔇 Stopped listening")
      this.updateVoiceStatus("idle")
    }
  }

  // Text-to-Speech Setup
  initializeTextToSpeech() {
    // Load ElevenLabs voices
    this.loadElevenLabsVoices()
    
    // Keep browser TTS as fallback
    if ('speechSynthesis' in window) {
      this.browserTTSAvailable = true
    }
  }

  async loadElevenLabsVoices() {
    try {
      console.log('🎙️ Loading ElevenLabs voices...')
      const response = await fetch('/chat/voices', {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        if (response.status === 403) {
          console.warn('⚠️ Premium subscription required for ElevenLabs voices')
          const errorData = await response.json().catch(() => ({}))
          console.log('Falling back to browser voices')
        } else {
          console.error(`❌ HTTP error! status: ${response.status}`)
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      console.log(`✅ Loaded ${data.presets?.length || 0} presets and ${data.voices?.length || 0} voices`)

      if (this.hasVoiceSelectTarget) {
        this.voiceSelectTarget.innerHTML = ''

        // Add preset voices optimized for driving
        if (data.presets && data.presets.length > 0) {
          const presetGroup = document.createElement('optgroup')
          presetGroup.label = 'Optimized for Driving'

          data.presets.forEach(preset => {
            const option = document.createElement('option')
            option.value = `elevenlabs:${preset.preset}`
            option.textContent = preset.name
            option.dataset.description = preset.description
            presetGroup.appendChild(option)
          })

          this.voiceSelectTarget.appendChild(presetGroup)
        }

        // Add all available voices
        if (data.voices && data.voices.length > 0) {
          const allVoicesGroup = document.createElement('optgroup')
          allVoicesGroup.label = 'All Voices'

          data.voices.forEach(voice => {
            const option = document.createElement('option')
            option.value = `elevenlabs:${voice.voice_id}`
            option.textContent = voice.name
            if (voice.labels) {
              option.textContent += ` (${Object.values(voice.labels).join(', ')})`
            }
            allVoicesGroup.appendChild(option)
          })

          this.voiceSelectTarget.appendChild(allVoicesGroup)
        }

        // Add browser TTS as fallback
        if (this.browserTTSAvailable) {
          const browserGroup = document.createElement('optgroup')
          browserGroup.label = 'Browser Voices (Fallback)'

          const browserOption = document.createElement('option')
          browserOption.value = 'browser:default'
          browserOption.textContent = 'System Default'
          browserGroup.appendChild(browserOption)

          this.voiceSelectTarget.appendChild(browserGroup)
        }

        // Set default to friendly female (Rachel)
        this.voiceSelectTarget.value = 'elevenlabs:dave'
        console.log('🎤 ElevenLabs voices loaded successfully')
      }
    } catch (error) {
      console.error('❌ Failed to load ElevenLabs voices:', error)
      this.fallbackToBrowserVoices()
    }
  }

  fallbackToBrowserVoices() {
    if (!this.browserTTSAvailable || !this.hasVoiceSelectTarget) return
    
    const voices = window.speechSynthesis.getVoices()
    this.voiceSelectTarget.innerHTML = '<option value="browser:default">System Default</option>'
    
    voices.forEach(voice => {
      const option = document.createElement('option')
      option.value = `browser:${voice.name}`
      option.textContent = `${voice.name} (${voice.lang})`
      this.voiceSelectTarget.appendChild(option)
    })
  }

  // Voice Input Methods
  startListening(event) {
    event.preventDefault()

    // Unlock audio context on first user interaction
    if (!this.audioContextUnlocked) {
      this.unlockAudioContext()
    }

    if (!this.recognition) {
      if (this.hasVoiceOnlyTextTarget) {
        this.voiceOnlyTextTarget.textContent = "Voice input not available"
      }
      return
    }

    this.isListening = true
    if (this.hasMicIconTarget) {
      this.micIconTarget.classList.add("text-red-500", "animate-pulse")
    }
    if (this.hasVoiceOnlyTextTarget) {
      this.voiceOnlyTextTarget.textContent = "Listening..."
    }

    this.startVoiceVisualizer()
    this.recognition.start()
  }

  unlockAudioContext() {
    // Create and immediately pause a silent audio element to unlock browser autoplay
    const silentAudio = new Audio('data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABhADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMD/////////////////////////////////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//sQZAAP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV//sQZAQP8AAAaQAAAAgAAA0gAAABAAABpAAAACAAADSAAAAEVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVU=')
    silentAudio.play().then(() => {
      console.log('🔓 Audio context unlocked')
      this.audioContextUnlocked = true
      silentAudio.pause()
    }).catch(err => {
      console.warn('Failed to unlock audio context:', err)
    })
  }

  stopListening(event) {
    event.preventDefault()

    if (!this.isListening) return

    this.isListening = false
    if (this.hasMicIconTarget) {
      this.micIconTarget.classList.remove("text-red-500", "animate-pulse")
    }
    this.stopVoiceVisualizer()
    
    if (this.recognition) {
      this.recognition.stop()
    }
  }

  processVoiceInput(transcript) {
    // Check for coach information
    this.detectCoachInfo(transcript)
    
    // Check for parts/service mentions
    this.detectPartsAndServices(transcript)
    
    // Send to Axle
    this.sendMessage(transcript)
  }

  // Text-to-Speech Methods
  async speak(text) {
    console.log('🎯 Speak method called with text:', text.substring(0, 50) + '...')
    const selectedVoice = this.voiceSelectTarget?.value || 'elevenlabs:dave'
    console.log('🎤 Selected voice:', selectedVoice)

    // Start visualizer if in voice mode
    if (this.currentMode === 'voice') {
      this.startVoiceVisualizer()
    }

    // Determine if using ElevenLabs or browser TTS
    if (selectedVoice.startsWith('elevenlabs:')) {
      console.log('🎵 Using ElevenLabs TTS')
      await this.speakWithElevenLabs(text, selectedVoice.replace('elevenlabs:', ''))
    } else {
      console.log('🔊 Using browser TTS')
      this.speakWithBrowser(text, selectedVoice.replace('browser:', ''))
    }
  }
  
  // Voice Visualizer Methods
  startVoiceVisualizer() {
    if (!this.hasVoiceVisualizerTarget) return

    // Create animated orbs like ChatGPT
    this.voiceVisualizerTarget.innerHTML = `
      <div class="voice-visualizer-container">
        <div class="voice-orb voice-orb-1"></div>
        <div class="voice-orb voice-orb-2"></div>
        <div class="voice-orb voice-orb-3"></div>
        <div class="voice-orb voice-orb-4"></div>
      </div>
    `

    this.voiceVisualizerTarget.classList.remove('hidden')
  }

  stopVoiceVisualizer() {
    if (this.hasVoiceVisualizerTarget) {
      this.voiceVisualizerTarget.classList.add('hidden')
    }
  }

  // Mode switching
  toggleMode() {
    if (this.currentMode === 'text') {
      this.switchToVoiceMode()
    } else {
      this.switchToTextMode()
    }
  }

  switchToTextMode() {
    this.currentMode = 'text'

    if (this.hasTextModeTarget) {
      this.textModeTarget.classList.remove('hidden')
    }

    if (this.hasVoiceModeTarget) {
      this.voiceModeTarget.classList.add('hidden')
    }

    if (this.hasModeToggleTarget) {
      this.modeToggleTarget.innerHTML = '<i class="fas fa-microphone"></i> Voice Mode'
    }

    this.stopVoiceVisualizer()
  }

  switchToVoiceMode() {
    this.currentMode = 'voice'

    if (this.hasTextModeTarget) {
      this.textModeTarget.classList.add('hidden')
    }

    if (this.hasVoiceModeTarget) {
      this.voiceModeTarget.classList.remove('hidden')
    }

    if (this.hasModeToggleTarget) {
      this.modeToggleTarget.innerHTML = '<i class="fas fa-comment"></i> Text Mode'
    }

    // Update voice-only text if there's a response
    if (this.hasVoiceOnlyTextTarget && this.hasResponseTextTarget) {
      const latestText = this.responseTextTarget.textContent
      if (latestText && latestText !== 'Hold the button and tell me about your coach') {
        this.voiceOnlyTextTarget.textContent = 'Listening...'
      }
    }
  }
  
  async speakWithElevenLabs(text, voice) {
    try {
      this.updateSpeakButton(true)
      
      // Stop any current audio
      if (this.currentAudio) {
        this.currentAudio.pause()
        this.currentAudio = null
      }
      
      // Create form data for the request
      const formData = new FormData()
      formData.append('text', text)
      formData.append('voice', voice)
      formData.append('authenticity_token', document.querySelector('[name="csrf-token"]').content)
      
      // Fetch audio from ElevenLabs
      const response = await fetch('/chat/speak', {
        method: 'POST',
        body: formData
      })
      
      if (!response.ok) {
        throw new Error('Failed to generate speech')
      }
      
      // Create audio element and play
      const audioBlob = await response.blob()
      const audioUrl = URL.createObjectURL(audioBlob)
      
      this.currentAudio = new Audio(audioUrl)
      this.currentAudio.playbackRate = parseFloat(this.voiceSpeedTarget?.value || 1)

      this.currentAudio.onplay = () => {
        if (this.currentMode === 'voice') {
          this.startVoiceVisualizer()
        }
      }

      this.currentAudio.onended = () => {
        this.updateSpeakButton(false)
        this.stopVoiceVisualizer()
        URL.revokeObjectURL(audioUrl)
        this.currentAudio = null
      }
      
      this.currentAudio.onerror = () => {
        console.error('Audio playback error')
        this.updateSpeakButton(false)
        // Fallback to browser TTS
        this.speakWithBrowser(text)
      }
      
      await this.currentAudio.play()
    } catch (error) {
      console.error('ElevenLabs TTS error:', error)
      this.updateSpeakButton(false)
      // Fallback to browser TTS
      await this.speakWithBrowser(text)
    }
  }
  
  speakWithBrowser(text, voiceName) {
    return new Promise((resolve, reject) => {
      if (!('speechSynthesis' in window)) {
        console.warn('Browser TTS not available')
        reject(new Error('Browser TTS not available'))
        return
      }

      // Cancel any ongoing speech
      window.speechSynthesis.cancel()

      const utterance = new SpeechSynthesisUtterance(text)

      // Apply settings
      utterance.rate = parseFloat(this.voiceSpeedTarget?.value || 1)

      if (voiceName && voiceName !== 'default') {
        const voices = window.speechSynthesis.getVoices()
        const voice = voices.find(v => v.name === voiceName)
        if (voice) utterance.voice = voice
      }

      utterance.onstart = () => {
        this.startVoiceVisualizer()
      }

      utterance.onend = () => {
        this.stopVoiceVisualizer()
        resolve()
      }

      utterance.onerror = (error) => {
        console.error('Browser TTS error:', error)
        this.updateSpeakButton(false)
        this.stopVoiceVisualizer()
        reject(error)
      }

      window.speechSynthesis.speak(utterance)
    })
  }

  stopSpeaking() {
    // Stop visualizer
    this.stopVoiceVisualizer()

    // Clear audio queue
    this.audioQueue = []
    this.isPlayingQueue = false

    // Stop ElevenLabs audio if playing
    if (this.currentAudio) {
      this.currentAudio.pause()
      this.currentAudio = null
    }

    // Stop browser TTS if speaking
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel()
    }

    this.updateSpeakButton(false)
  }

  toggleSpeaking() {
    // Check if currently speaking (either ElevenLabs or browser)
    const isSpeaking = this.currentAudio || window.speechSynthesis?.speaking
    
    if (isSpeaking) {
      this.stopSpeaking()
    } else {
      const responseText = this.responseTextTarget.textContent
      this.speak(responseText)
    }
  }

  updateSpeakButton(isSpeaking) {
    if (this.hasSpeakButtonTarget) {
      const icon = this.speakButtonTarget.querySelector('i')
      if (isSpeaking) {
        icon.classList.remove('fa-volume-up')
        icon.classList.add('fa-stop')
        this.speakButtonTarget.classList.add('bg-blue-600', 'text-white')
        this.speakButtonTarget.classList.remove('bg-gray-800', 'text-gray-400')
      } else {
        icon.classList.add('fa-volume-up')
        icon.classList.remove('fa-stop')
        this.speakButtonTarget.classList.remove('bg-blue-600', 'text-white')
        this.speakButtonTarget.classList.add('bg-gray-800', 'text-gray-400')
      }
    }
  }

  // Message Handling
  async sendMessage(message) {
    this.updateStatus("thinking")

    // Add user message to UI immediately
    this.addMessageToUI(message, 'user')

    // Clear input
    if (this.hasTextInputTarget) {
      this.textInputTarget.value = ''
    }

    // Use streaming if auto-speak is enabled for faster response
    if (this.autoSpeakEnabled) {
      this.sendMessageStreaming(message)
    } else {
      this.sendMessageNonStreaming(message)
    }
  }

  // Streaming version for voice mode - immediate response
  async sendMessageStreaming(message) {
    try {
      // Clear any existing audio queue
      this.audioQueue = []
      this.isPlayingQueue = false

      // Prepare for streaming response
      this.responseTextTarget.innerHTML = '<div class="normal-response-text streaming-text"></div>'
      const streamingContainer = this.responseTextTarget.querySelector('.streaming-text')
      let fullResponse = ''

      // Create EventSource connection
      const url = new URL('/chat/stream', window.location.origin)
      const formData = new URLSearchParams()
      formData.append('message', message)
      formData.append('authenticity_token', document.querySelector('[name="csrf-token"]').content)

      const eventSource = new EventSource(url + '?' + formData.toString(), {
        method: 'POST'
      })

      // For EventSource, we need to use POST via a workaround
      // Let's use fetch with a special header instead
      eventSource.close()

      // Use fetch with stream reading instead
      const response = await fetch('/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({ message: message })
      })

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Process complete SSE messages
        const lines = buffer.split('\n\n')
        buffer = lines.pop() // Keep incomplete message in buffer

        for (const message of lines) {
          if (!message.trim()) continue

          const eventMatch = message.match(/^event: (.+)$/m)
          const dataMatch = message.match(/^data: (.+)$/m)

          if (!eventMatch || !dataMatch) continue

          const eventType = eventMatch[1]
          const data = JSON.parse(dataMatch[1])

          if (eventType === 'token') {
            // Add token to display in real-time
            fullResponse += data.token
            streamingContainer.textContent = fullResponse
          } else if (eventType === 'sentence') {
            // Generate audio for complete sentence
            console.log('📝 Complete sentence received:', data.sentence.substring(0, 50) + '...')
            this.queueSentenceAudio(data.sentence)
          } else if (eventType === 'done') {
            console.log('✅ Streaming complete')
            this.updateStatus("ready")

            // Scan for context in complete response
            this.detectCoachInfo(fullResponse)
            this.detectPartsAndServices(fullResponse)
            break
          } else if (eventType === 'error') {
            console.error('Streaming error:', data.error)

            // Daily AI cap or rate limit: say so plainly
            if (data.cap_reached) {
              this.updateStatus("ready")
              streamingContainer.textContent = data.error
            } else if (data.rate_limited) {
              this.updateStatus("error")
              streamingContainer.innerHTML = `<p class="text-yellow-400">${data.error} (Limit: ${data.limit}/min)</p>`
            } else {
              this.updateStatus("error")
              streamingContainer.innerHTML = '<p class="text-red-400">Sorry, I encountered an error. Please try again.</p>'
            }
            break
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error)
      this.updateStatus("error")
      this.responseTextTarget.innerHTML = '<p class="text-red-400">Sorry, I encountered an error. Please try again.</p>'
    }
  }

  // Non-streaming version for text mode
  async sendMessageNonStreaming(message) {
    try {
      const response = await fetch('/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ message: message })
      })

      const data = await response.json()

      if (data.success && data.messages) {
        // Find the assistant's response
        const assistantMessage = data.messages.find(m => m.role === 'assistant')
        if (assistantMessage) {
          this.displayResponse(assistantMessage.formatted_content || assistantMessage.content)
        }
      }
    } catch (error) {
      console.error('Chat error:', error)
      this.updateStatus("error")
      this.responseTextTarget.innerHTML = '<p class="text-red-400">Sorry, I encountered an error. Please try again.</p>'
    }

    this.updateStatus("ready")
  }

  // Queue audio for a sentence and start playing if not already playing
  async queueSentenceAudio(sentence) {
    console.log('🎵 Queueing audio for sentence:', sentence.substring(0, 50) + '...')

    const selectedVoice = this.voiceSelectTarget?.value || 'elevenlabs:dave'
    const voiceId = selectedVoice.replace('elevenlabs:', '')

    // Add to queue
    this.audioQueue.push({
      text: sentence,
      voice: voiceId
    })

    // Start playing queue if not already playing
    if (!this.isPlayingQueue) {
      this.playAudioQueue()
    }
  }

  // Play audio queue progressively
  async playAudioQueue() {
    if (this.isPlayingQueue || this.audioQueue.length === 0) return

    this.isPlayingQueue = true
    console.log('▶️ Starting audio queue playback')

    while (this.audioQueue.length > 0) {
      const item = this.audioQueue.shift()

      try {
        await this.generateAndPlayAudio(item.text, item.voice)
      } catch (error) {
        console.error('Error playing audio:', error)
        // Continue with next item even if this one fails
      }
    }

    this.isPlayingQueue = false
    this.stopVoiceVisualizer()
    console.log('⏹️ Audio queue playback complete')
  }

  // Generate and play a single audio chunk
  async generateAndPlayAudio(text, voice) {
    return new Promise(async (resolve, reject) => {
      try {
        console.log('🎵 Generating audio for:', text.substring(0, 50) + '...')
        console.log('🎤 Using voice:', voice)

        // Create form data for the request
        const formData = new FormData()
        formData.append('text', text)
        formData.append('voice', voice)
        formData.append('authenticity_token', document.querySelector('[name="csrf-token"]').content)

        // Fetch audio from ElevenLabs
        console.log('📡 Fetching audio from /chat/speak...')
        const response = await fetch('/chat/speak', {
          method: 'POST',
          body: formData
        })

        console.log('📡 Response status:', response.status, response.statusText)

        // Check if it's a JSON error response (quota exceeded)
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json()

          if (data.quota_exceeded || data.fallback_to_browser) {
            console.warn('⚠️ Premium voice unavailable, falling back to browser TTS')
            console.log('Quota info:', data.quota_info)

            // Fall back to browser TTS
            await this.speakWithBrowser(text)
            resolve()
            return
          }

          throw new Error(data.error || 'Failed to generate speech')
        }

        if (!response.ok) {
          throw new Error('Failed to generate speech')
        }

        // Create audio element and play
        const audioBlob = await response.blob()
        console.log('🎵 Audio blob size:', audioBlob.size, 'bytes')
        const audioUrl = URL.createObjectURL(audioBlob)

        const audio = new Audio(audioUrl)
        audio.playbackRate = parseFloat(this.voiceSpeedTarget?.value || 1)

        audio.onplay = () => {
          console.log('▶️ Audio playback started')
          this.startVoiceVisualizer()
        }

        audio.onended = () => {
          console.log('⏹️ Audio playback ended')
          this.stopVoiceVisualizer()
          URL.revokeObjectURL(audioUrl)
          resolve()
        }

        audio.onerror = (error) => {
          console.error('Audio playback error:', error)
          URL.revokeObjectURL(audioUrl)
          reject(new Error('Audio playback failed'))
        }

        console.log('🔊 Starting audio playback...')
        try {
          await audio.play()
          console.log('✅ Audio playback started successfully')
        } catch (err) {
          console.error('❌ Failed to start audio playback:', err.name, err.message)

          // If autoplay is blocked, unlock and retry
          if (err.name === 'NotAllowedError') {
            console.warn('⚠️ Autoplay blocked - audio context may not be unlocked')
            this.unlockAudioContext()
          }

          reject(err)
        }
      } catch (error) {
        console.error('Audio generation error:', error)

        // Fall back to browser TTS on any error
        try {
          console.log('🔊 Falling back to browser TTS')
          await this.speakWithBrowser(text)
          resolve()
        } catch (fallbackError) {
          reject(fallbackError)
        }
      }
    })
  }

  submitText(event) {
    event.preventDefault()
    
    const message = this.textInputTarget.value.trim()
    if (message) {
      this.sendMessage(message)
    }
  }

  displayResponse(content) {
    // Wrap the content in the normal response text class
    this.responseTextTarget.innerHTML = `<div class="normal-response-text">${content}</div>`

    // Scan for context
    this.detectCoachInfo(content)
    this.detectPartsAndServices(content)
  }

  addMessageToUI(content, role) {
    if (this.hasMessagesTarget) {
      const messageDiv = document.createElement('div')
      messageDiv.className = role === 'user' ? 'text-right' : 'text-left'
      
      const now = new Date()
      const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      
      messageDiv.innerHTML = `
        <div class="inline-block max-w-2xl">
          <div class="${role === 'user' ? 
            'bg-blue-600 text-white' : 
            'bg-gray-800 text-gray-200'} rounded-2xl px-6 py-4">
            <p>${content}</p>
          </div>
          <p class="text-xs text-gray-500 mt-1 px-2">${time}</p>
        </div>
      `
      
      this.messagesTarget.appendChild(messageDiv)
    }
  }

  // Coach Detection
  detectCoachInfo(text) {
    const lowerText = text.toLowerCase()
    
    // Look for VIN pattern
    const vinPattern = /\b[A-HJ-NPR-Z0-9]{17}\b/i
    const vinMatch = text.match(vinPattern)
    
    if (vinMatch) {
      this.updateCoachInfo(`VIN: ${vinMatch[0]}`)
      this.showCoachCard(vinMatch[0])
      return
    }
    
    // Look for year/make/model mentions
    const coachPatterns = [
      /(\d{4})\s+(prevost|mci|vanhool|setra|volvo)/i,
      /(prevost|mci|vanhool|setra|volvo)\s+(\w+)/i,
      /my\s+coach\s+is\s+a?\s+(.+)/i
    ]
    
    for (const pattern of coachPatterns) {
      const match = text.match(pattern)
      if (match) {
        this.updateCoachInfo(match[0])
        this.showCoachCard(match[0])
        break
      }
    }
  }

  detectCoachFromHistory() {
    // Check existing messages for coach info
    const messages = this.element.querySelectorAll('[data-message-content]')
    messages.forEach(msg => {
      this.detectCoachInfo(msg.textContent)
    })
  }

  updateCoachInfo(info) {
    if (this.hasCoachStatusTarget) {
      this.coachStatusTarget.innerHTML = `
        <i class="fas fa-bus text-green-500 mr-1"></i>
        ${info}
      `
    }
  }

  showCoachCard(coachInfo) {
    if (this.hasCoachCardTarget) {
      this.coachCardTarget.classList.remove('hidden')
      this.coachInfoTarget.textContent = coachInfo
    }
  }

  detectCoach() {
    // Prompt user to provide coach info
    this.sendMessage("I need help identifying my coach. Can you help me figure out what model I have?")
  }

  // Parts & Service Detection
  detectPartsAndServices(text) {
    const lowerText = text.toLowerCase()
    
    const partKeywords = [
      'oil filter', 'air filter', 'brake', 'tire', 'battery',
      'coolant', 'transmission', 'engine', 'alternator', 'starter',
      'belt', 'hose', 'windshield', 'wiper', 'headlight', 'taillight'
    ]
    
    const serviceKeywords = [
      'mechanic', 'service', 'repair', 'maintenance', 'inspection',
      'oil change', 'tune up', 'alignment', 'diagnostic'
    ]
    
    const detectedParts = partKeywords.filter(keyword => lowerText.includes(keyword))
    const detectedServices = serviceKeywords.filter(keyword => lowerText.includes(keyword))
    
    if (detectedParts.length > 0 || detectedServices.length > 0) {
      this.showSuggestions(detectedParts, detectedServices)
    }
  }

  scanForPartsAndServices() {
    // Scan existing response for parts/services
    if (this.hasResponseTextTarget) {
      this.detectPartsAndServices(this.responseTextTarget.textContent)
    }
  }

  showSuggestions(parts, services) {
    if (!this.hasSuggestionCardTarget) return
    
    this.suggestionCardTarget.classList.remove('hidden')
    
    let suggestionsHTML = ''
    
    if (parts.length > 0) {
      suggestionsHTML += `
        <div class="text-sm">
          <p class="text-xs text-gray-500 mb-1">Parts Mentioned:</p>
          <div class="flex flex-wrap gap-2">
            ${parts.map(part => `
              <button class="px-3 py-1 bg-blue-600/20 text-blue-400 rounded-full text-xs hover:bg-blue-600/30">
                ${part}
              </button>
            `).join('')}
          </div>
        </div>
      `
    }
    
    if (services.length > 0) {
      suggestionsHTML += `
        <div class="text-sm">
          <p class="text-xs text-gray-500 mb-1">Services Needed:</p>
          <div class="flex flex-wrap gap-2">
            ${services.map(service => `
              <button class="px-3 py-1 bg-yellow-600/20 text-yellow-400 rounded-full text-xs hover:bg-yellow-600/30">
                ${service}
              </button>
            `).join('')}
          </div>
        </div>
      `
    }
    
    // Add find nearby button
    suggestionsHTML += `
      <button data-action="click->voice-chat#findNearby" 
              class="mt-3 w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors">
        <i class="fas fa-map-marker-alt mr-2"></i>
        Find Nearby Services
      </button>
    `
    
    this.suggestionsTarget.innerHTML = suggestionsHTML
  }

  async findNearby() {
    // Use existing location or prompt for one
    const location = this.locationTextTarget?.textContent
    
    if (location === "Location Unknown") {
      this.updateLocation()
    } else {
      this.sendMessage(`Find service centers and parts stores near ${location}`)
    }
  }

  // Location Management
  updateLocation() {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser")
      return
    }
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords
        
        try {
          const response = await fetch('/chat/update_location', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
            },
            body: JSON.stringify({ latitude, longitude })
          })
          
          const data = await response.json()
          
          if (data.success) {
            this.locationTextTarget.textContent = data.location
          }
        } catch (error) {
          console.error('Location update error:', error)
        }
      },
      (error) => {
        console.error('Geolocation error:', error)
        alert("Unable to get your location. Please check your settings.")
      }
    )
  }

  // UI State Management
  updateStatus(status) {
    // Update status text in simplified UI
    if (this.hasVoiceOnlyTextTarget) {
      switch(status) {
        case 'ready':
          this.voiceOnlyTextTarget.textContent = 'Tap and hold to speak'
          break
        case 'thinking':
          this.voiceOnlyTextTarget.textContent = 'Thinking...'
          break
        case 'listening':
          this.voiceOnlyTextTarget.textContent = 'Listening...'
          break
        case 'error':
          this.voiceOnlyTextTarget.textContent = 'Error - please try again'
          break
      }
    }

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = status

      // Update status indicator if it exists
      const indicator = document.querySelector('[data-voice-chat-target="status"]')?.parentElement?.querySelector('.bg-green-500')
      if (indicator) {
        indicator.classList.remove('bg-green-500', 'bg-yellow-500', 'bg-red-500')

        switch(status) {
          case 'ready':
            indicator.classList.add('bg-green-500')
            break
          case 'thinking':
          case 'listening':
            indicator.classList.add('bg-yellow-500')
            break
          case 'error':
            indicator.classList.add('bg-red-500')
            break
        }
      }
    }
  }

  updateVoiceStatus(status) {
    if (this.hasVoiceStatusTarget) {
      let html = ''
      
      switch(status) {
        case 'listening':
          html = '<i class="fas fa-microphone text-red-500 animate-pulse mr-1"></i><span class="text-red-500">Listening...</span>'
          break
        case 'idle':
          html = '<i class="fas fa-microphone-slash text-gray-400 mr-1"></i><span class="text-gray-400">Voice Off</span>'
          break
        case 'error':
          html = '<i class="fas fa-exclamation-triangle text-yellow-500 mr-1"></i><span class="text-yellow-500">Voice Error</span>'
          break
      }
      
      this.voiceStatusTarget.innerHTML = html
    }
  }

  async toggleHistory() {
    if (this.hasHistoryPanelTarget) {
      const isHidden = this.historyPanelTarget.classList.contains('hidden')

      if (isHidden) {
        // Fetch session history when opening
        await this.loadSessionHistory()
      }

      this.historyPanelTarget.classList.toggle('hidden')
    }
  }

  async loadSessionHistory() {
    try {
      const response = await fetch('/chat/sessions', {
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Failed to load sessions')

      const data = await response.json()
      this.renderSessionHistory(data.sessions)
    } catch (error) {
      console.error('Error loading session history:', error)
    }
  }

  renderSessionHistory(sessions) {
    if (!this.hasHistoryPanelTarget) return

    const container = this.historyPanelTarget.querySelector('[data-voice-chat-target="sessionList"]')
    if (!container) return

    if (sessions.length === 0) {
      container.innerHTML = '<p class="text-white/50 text-center py-8">No previous sessions</p>'
      return
    }

    container.innerHTML = sessions.map(session => `
      <a href="/chat/${session.id}"
         class="block p-4 bg-white/10 backdrop-blur rounded-xl hover:bg-white/20 transition-all">
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <h3 class="font-medium text-white">${session.title}</h3>
            <p class="text-sm text-white/50 mt-1">
              ${new Date(session.started_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}
            </p>
          </div>
          <div class="text-xs text-white/50 bg-white/10 px-2 py-1 rounded">
            ${session.message_count} messages
          </div>
        </div>
      </a>
    `).join('')
  }

  toggleSettings() {
    if (this.hasSettingsPanelTarget) {
      this.settingsPanelTarget.classList.toggle('hidden')
    }
  }
  
  // Text input is now always visible, so this method is no longer needed
  toggleTextInput() {
    // Focus the always-visible text input
    this.textInputTarget?.focus()
  }

  // Settings & Preferences
  toggleAutoSpeak() {
    this.autoSpeakEnabled = !this.autoSpeakEnabled
    localStorage.setItem('axle_auto_speak', this.autoSpeakEnabled)

    // Save to server
    this.saveSettingsToServer('auto_speak', this.autoSpeakEnabled)

    if (this.hasAutoSpeakButtonTarget) {
      if (this.autoSpeakEnabled) {
        this.autoSpeakButtonTarget.classList.add('bg-blue-600', 'text-white')
        this.autoSpeakButtonTarget.classList.remove('bg-gray-800', 'text-gray-400')
      } else {
        this.autoSpeakButtonTarget.classList.remove('bg-blue-600', 'text-white')
        this.autoSpeakButtonTarget.classList.add('bg-gray-800', 'text-gray-400')
      }
    }

    if (this.hasAutoSpeakToggleTarget) {
      this.autoSpeakToggleTarget.checked = this.autoSpeakEnabled
    }
  }

  toggleHandsFree() {
    this.handsFreeEnabled = !this.handsFreeEnabled
    localStorage.setItem('axle_hands_free', this.handsFreeEnabled)

    // Save to server
    this.saveSettingsToServer('hands_free', this.handsFreeEnabled)

    if (this.handsFreeEnabled) {
      this.startHandsFreeListening()
    } else {
      this.stopHandsFreeListening()
    }
  }

  onVoiceChange(event) {
    const selectedVoice = event.target.value
    console.log('Voice changed to:', selectedVoice)
    this.saveSettingsToServer('voice', selectedVoice)
  }

  onSpeedChange(event) {
    const speed = parseFloat(event.target.value)
    console.log('Speed changed to:', speed)
    this.saveSettingsToServer('speed', speed)
  }

  startHandsFreeListening() {
    // Implement wake word detection
    console.log("🎧 Hands-free mode enabled - say 'Hey Axle' to activate")
    // This would require continuous listening with wake word detection
    // For now, we'll just log it
  }

  stopHandsFreeListening() {
    console.log("🔇 Hands-free mode disabled")
  }

  async loadPreferences() {
    // Try to load preferences from user settings via data attribute
    const userSettings = this.element.dataset.userVoiceSettings

    if (userSettings) {
      try {
        const settings = JSON.parse(userSettings)

        // Load voice preference
        if (settings.voice && this.hasVoiceSelectTarget) {
          this.voiceSelectTarget.value = settings.voice
        }

        // Load auto-speak preference
        this.autoSpeakEnabled = settings.auto_speak !== false  // Default to true
        if (this.hasAutoSpeakToggleTarget) {
          this.autoSpeakToggleTarget.checked = this.autoSpeakEnabled
        }

        // Load speed preference
        if (settings.speed && this.hasVoiceSpeedTarget) {
          this.voiceSpeedTarget.value = settings.speed
        }

        // Load hands-free preference
        this.handsFreeEnabled = settings.hands_free === true
        if (this.hasHandsFreeToggleTarget) {
          this.handsFreeToggleTarget.checked = this.handsFreeEnabled
        }

        console.log('✅ Loaded voice settings from profile:', settings)
      } catch (error) {
        console.error('Failed to parse user voice settings:', error)
        this.loadDefaultPreferences()
      }
    } else {
      this.loadDefaultPreferences()
    }
  }

  loadDefaultPreferences() {
    // Fallback to localStorage or defaults
    const savedAutoSpeak = localStorage.getItem('axle_auto_speak')
    this.autoSpeakEnabled = savedAutoSpeak === null ? true : savedAutoSpeak === 'true'

    this.handsFreeEnabled = localStorage.getItem('axle_hands_free') === 'true'

    console.log('🔊 Auto-speak enabled (default):', this.autoSpeakEnabled)

    if (this.hasAutoSpeakToggleTarget) {
      this.autoSpeakToggleTarget.checked = this.autoSpeakEnabled
    }

    if (this.handsFreeEnabled && this.hasHandsFreeToggleTarget) {
      this.handsFreeToggleTarget.checked = true
    }
  }

  async saveSettingsToServer(settingType, value) {
    try {
      const settings = {}
      settings[settingType] = value

      const response = await fetch('/chat/update_voice_settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify(settings)
      })

      const data = await response.json()
      if (data.success) {
        console.log(`✅ Saved ${settingType} to profile:`, value)
      } else {
        console.error(`Failed to save ${settingType}:`, data.error)
      }
    } catch (error) {
      console.error('Error saving settings:', error)
    }
  }
}